import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
  Radio,
} from '@mui/material';
import { ProfileTypes, TargetViewers } from 'constants/profileType.constant';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import PostAuthorProfile from '../next-page-components/PostAuthorProfile';
import useUserProfile from '@/hooks/useUserProfile';
import Avatar from '../next-ui/Avatar';
import CustomTypography from '../next-ui/Typography';
import TimeSincePost from '../next-page-components/TimeSincePost';
import Tooltip from '../next-ui/Tooltip';
import { BiWorld } from 'react-icons/bi';
import { MdVpnLock } from 'react-icons/md';

function PublishToPublicPage(props) {
  const { isAdmin, isNekiAdmin, isP2PFundraiser } = props;
  const { getValues, watch, setValue } = useFormContext();
  const [targetViewers, isFeatured, postAs, postOrg] = watch([
    'targetViewers',
    'isFeatured',
    'postAs',
    'postOrg',
  ]);
  const isPublic = targetViewers === TargetViewers.All;
  const user = useUserProfile();

  const toggleIsPublic = () => {
    if (isPublic) {
      setValue('targetViewers', TargetViewers.MyOrg);
      setValue('isFeatured', false);
    } else {
      setValue('targetViewers', TargetViewers.All);
    }
  };

  const toggleIsFeatured = () => {
    setValue('isFeatured', !isFeatured);
  };

  const toggleAuthorType = (authorType) => {
    setValue('postAs', authorType);
  };

  if (isP2PFundraiser) {
    return (
      <Stack gap={1}>
        <Typography variant="subtitle1">
          Your post will be visible on your community&apos;s public page.
          Once you post this, we&apos;ll provide a link that you can share with
          your friends, family and peers.
        </Typography>
      </Stack>
    );
  }

  const renderAuthorPreview = (authorType) => {
    const preview = {
      author: {
        name: user?.fullName,
        avatar: user?.avatar,
        slug: '',
      },
      authorOrg: {
        name: isNekiAdmin ? postOrg?.name : user?.activeCommunity?.name,
        avatar: isNekiAdmin
          ? postOrg?.logo_url
          : user?.activeCommunity?.logo_url || user?.activeCommunity?.logoUrl,
        slug: '',
      },
      createdOn: Date.now(),
      targetViewers: 'all',
      targetOrgs: [],
      renderProps: {
        nonProfit: {},
        isPromotionPost: false,
      },
    };
    const { author, authorOrg, createdOn, targetViewers, targetOrgs } = preview;
    const { nonProfit, isPromotionPost } = preview.renderProps || {};
    const { name, avatar, slug } =
      authorType === ProfileTypes.Individual ? author : authorOrg;
    return (
      <div className="flex items-center justify-start gap-2.5 lg:gap-4">
        <CustomTypography>Posted by</CustomTypography>
        <Avatar src={avatar} alt={name} size="sm" />
        <div className="flex-row items-start justify-around leading-snug">
          <div className="flex flex-wrap items-center gap-x-1">
            <CustomTypography
              variant="h5"
              component="h1"
              className={slug && 'group-hover:text-text-hyperlink'}>
              {name}
            </CustomTypography>
          </div>
          <div className="flex items-center gap-2">
            {!isPromotionPost && (
              <TimeSincePost variant="body2" createdOn={createdOn} />
            )}
            <>
              {targetViewers === TargetViewers.All && (
                <Tooltip content="This post is visible to everyone">
                  <BiWorld className="h-4 w-4 text-text-secondary" />
                </Tooltip>
              )}
              {targetViewers === TargetViewers.MyOrg && (
                <Tooltip content="This post is visible only to your community">
                  <MdVpnLock className="h-4 w-4 text-text-secondary" />
                </Tooltip>
              )}
            </>
          </div>
        </div>
      </div>
    );
  };

  if (isAdmin) {
    return (
      <Stack gap={1}>
        <Typography variant="subtitle1">
          {isPublic
            ? 'This post will be visible to all members and on your community public page.'
            : 'This post can only be viewed by your community members.'}
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                disableRipple
                size="small"
                checked={isPublic}
                onChange={toggleIsPublic}
              />
            }
            label={
              <Typography variant="body2">
                Make this post visible on your community&apos;s public page.
              </Typography>
            }
          />
          {isPublic && (
            <Stack marginLeft={3}>
              <FormControlLabel
                control={
                  <Radio
                    disableRipple
                    size="small"
                    checked={postAs === ProfileTypes.Organization}
                    onChange={() => toggleAuthorType(ProfileTypes.Organization)}
                  />
                }
                label={
                  <Typography variant="body2" color="text.primary">
                    {renderAuthorPreview(ProfileTypes.Organization)}
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Radio
                    disableRipple
                    size="small"
                    checked={postAs === ProfileTypes.Individual}
                    onChange={() => toggleAuthorType(ProfileTypes.Individual)}
                  />
                }
                label={
                  <Typography variant="body2" color="text.primary">
                    {renderAuthorPreview(ProfileTypes.Individual)}
                  </Typography>
                }
              />
            </Stack>
          )}
          <FormControlLabel
            control={
              <Checkbox
                disableRipple
                size="small"
                disabled={!isPublic}
                checked={isFeatured}
                onChange={toggleIsFeatured}
              />
            }
            label={
              <Typography
                variant="body2"
                color={isPublic ? 'text.primary' : 'text.disabled'}>
                Feature this post on your organzation&apos;s public page.
              </Typography>
            }
          />
        </FormGroup>
      </Stack>
    );
  }

  return (
    <Stack gap={1}>
      <Typography variant="subtitle1">
        {isPublic
          ? 'This post will be visible to all members and on your community’s public page.'
          : 'This post can only be viewed by your community members.'}
      </Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              disableRipple
              size="small"
              checked={isPublic}
              onChange={toggleIsPublic}
            />
          }
          label={
            <Typography variant="body2">
              Make this post visible on your community’s public page.
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              disableRipple
              size="small"
              disabled={!isPublic}
              checked={isFeatured}
              onChange={toggleIsFeatured}
            />
          }
          label={
            <Typography
              variant="body2"
              color={isPublic ? 'text.primary' : 'text.disabled'}>
              Feature this post on your organzation&apos;s public page.
            </Typography>
          }
        />
      </FormGroup>
    </Stack>
  );
}

export default PublishToPublicPage;
